(() => {
  'use strict';

  const ROOT_SELECTOR = '#tee-artwork-form';
  const WIZARD_ID = 'pk-tib-wizard';
  const HIDDEN_CLASS = 'pk-tib-hidden';
  const VISIBLE_CLASS = 'pk-tib-step-visible';

  /*
   * The pk-step-* classes are the preferred selectors. Add them in TeeInBlue
   * as described in the installation guide. The layer IDs are fallbacks for
   * the current Paws & Keeps campaigns.
   */
  const STEP_DEFINITIONS = [
    {
      id: 'format',
      label: 'Format',
      selector: '.tee-available-products',
      formStep: false,
      validationMessage: 'Please choose a format before continuing.'
    },
    {
      id: 'size',
      label: 'Sizing',
      selector: '.tee-variants',
      formStep: false,
      validationMessage: 'Please choose a size before continuing.'
    },
    {
      id: 'background',
      label: 'Background',
      selector: [
        '.pk-step-background',
        '#tee-field--layer-788951',
        '#tee-field--layer-655894',
        '#tee-field--layer-626875'
      ].join(', '),
      formStep: true,
      validationMessage: 'Please choose a background before continuing.'
    },
    {
      id: 'upload',
      label: 'Upload',
      selector: [
        '.pk-step-upload',
        '#tee-field--layer-249915',
        '#tee-field--layer-190251',
        '#tee-field--layer-303538',
        '.pk-step-pet-name',
        '#tee-field--layer-606222',
        '#tee-field--layer-124087',
        '#tee-field--layer-431318'
      ].join(', '),
      formStep: true,
      optional: true
    },
    {
      id: 'extras',
      label: 'Extras',
      selector: [
        '.pk-step-digital',
        '#tee-field--layer-986584',
        '#tee-field--layer-464286',
        '.pk-step-priority',
        '#tee-field--layer-747986',
        '#tee-field--layer-320017',
        '#tee-field--layer-748690',
        '.pk-step-notes',
        '#tee-field--layer-695981',
        '#tee-field--layer-279750',
        '#tee-field--layer-856635',
        '#tee-field--layer-720689'
      ].join(', '),
      formStep: true,
      optional: true,
      final: true
    }
  ];

  const sharedState = window.PawsTeeinblueWizardState || {
    currentStep: 'format',
    maxUnlockedIndex: 0
  };

  window.PawsTeeinblueWizardState = sharedState;

  let activeController = null;
  let refreshTimer = null;

  function unique(items) {
    return [...new Set(items.filter(Boolean))];
  }

  function getStepDefinition(stepId) {
    return STEP_DEFINITIONS.find((step) => step.id === stepId);
  }

  function renameFormatHeading(root) {
    const heading = root.querySelector(
      '.tee-available-products > .tee-option__title'
    );

    if (heading && heading.textContent.trim() !== 'Choose Format') {
      heading.textContent = 'Choose Format';
    }
  }

  function getFieldHeading(field) {
    return field
      ?.querySelector('.tee-field__heading > span:first-child')
      ?.textContent
      ?.trim() || '';
  }

  function getSemanticStepFields(root, step) {
    const headingPatterns = {
      background: [
        /^background(?:\s+(?:color|colour))?\b/i
      ],
      upload: [
        /\bupload\b.*\b(?:pet\s+)?photos?\b/i,
        /\bpet\s*name\b/i,
        /^text$/i
      ],
      extras: [
        /\bdigital\s+(?:copy|download)\b/i,
        /\bpriority(?:\s+(?:review|preview))?\b/i,
        /\bnotes?\b/i
      ]
    };
    const patterns = headingPatterns[step.id];
    if (!patterns) return [];

    return [...root.querySelectorAll('#teeFormInputs > .tee-field')].filter(
      (field) => patterns.some((pattern) => pattern.test(getFieldHeading(field)))
    );
  }

  function getStepNodes(root, step) {
    const matches = [
      ...root.querySelectorAll(step.selector),
      ...getSemanticStepFields(root, step)
    ];

    return unique(matches.map((element) => {
      // TeeInBlue may put a merchant-supplied custom class on a child element.
      // Always show/hide the complete field wrapper when one exists.
      return element.closest('#teeFormInputs > .tee-field') || element;
    }));
  }

  function isDigitalFileSelected(root) {
    const selectedCard = root.querySelector('.tee-available-products .tee-clipart.active');
    if (!selectedCard) return false;

    const titledLabel = selectedCard.querySelector('label[data-title]');
    const image = selectedCard.querySelector('img[alt]');
    const text = [
      titledLabel?.dataset.title,
      image?.alt,
      selectedCard.textContent
    ].filter(Boolean).join(' ');

    return /digital\s*(file|download)?/i.test(text);
  }

  function getActiveSteps(root) {
    if (!isDigitalFileSelected(root)) return STEP_DEFINITIONS;

    // A digital file has no physical print size, so omit that step.
    return STEP_DEFINITIONS.filter((step) => step.id !== 'size');
  }

  function setHidden(element, hidden) {
    if (!element) return;
    element.classList.toggle(HIDDEN_CLASS, hidden);
    element.setAttribute('aria-hidden', hidden ? 'true' : 'false');
  }

  function clearStepError(controller) {
    controller.error.textContent = '';
    controller.error.hidden = true;
  }

  function showStepError(controller, message, focusTarget) {
    controller.error.textContent = message;
    controller.error.hidden = false;

    const target = focusTarget || controller.wizard;
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });
  }

  function validateCurrentStep(controller) {
    const { root } = controller;
    const step = getStepDefinition(sharedState.currentStep);
    if (!step || step.optional) return true;

    const nodes = getStepNodes(root, step);
    if (!nodes.length) {
      showStepError(
        controller,
        'This option is still loading. Please wait a moment and try again.'
      );
      return false;
    }

    if (step.id === 'format' || step.id === 'background') {
      const radios = nodes.flatMap((node) => [
        ...node.querySelectorAll('input[type="radio"]:not(:disabled)')
      ]);
      const valid = radios.length > 0 && radios.some((radio) => radio.checked);

      if (!valid) {
        showStepError(controller, step.validationMessage, nodes[0]);
      }
      return valid;
    }

    if (step.id === 'size') {
      const radios = nodes.flatMap((node) => [
        ...node.querySelectorAll(
          '.tee-option--size input[type="radio"]:not(:disabled)'
        )
      ]);
      const selects = nodes.flatMap((node) => [
        ...node.querySelectorAll('.tee-option--size select:not(:disabled)')
      ]);
      const valid = radios.length > 0
        ? radios.some((radio) => radio.checked)
        : selects.some((select) => {
            const selectedOption = select.selectedOptions[0];
            return Boolean(select.value) && !selectedOption?.disabled;
          });

      if (!valid) {
        showStepError(controller, step.validationMessage, nodes[0]);
      }
      return valid;
    }

    return true;
  }

  function scrollToWizard(controller) {
    controller.wizard.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  }

  function render(controller) {
    const { root, wizard, controls } = controller;
    renameFormatHeading(root);
    const activeSteps = getActiveSteps(root);

    if (!activeSteps.some((step) => step.id === sharedState.currentStep)) {
      sharedState.currentStep = activeSteps[0].id;
    }

    const currentStep = getStepDefinition(sharedState.currentStep);
    const currentStaticIndex = STEP_DEFINITIONS.findIndex(
      (step) => step.id === currentStep.id
    );
    const currentActiveIndex = activeSteps.findIndex(
      (step) => step.id === currentStep.id
    );

    sharedState.maxUnlockedIndex = Math.max(
      sharedState.maxUnlockedIndex,
      currentStaticIndex
    );

    const controlledElements = unique([
      root.querySelector('.tee-available-products'),
      root.querySelector('.tee-variants'),
      ...root.querySelectorAll('#teeFormInputs > .tee-field')
    ]);

    const currentNodes = getStepNodes(root, currentStep);
    const currentNodeSet = new Set(currentNodes);
    root.dataset.pkCurrentStep = currentStep.id;

    controlledElements.forEach((element) => {
      const shouldShow = currentNodeSet.has(element);
      element.classList.toggle(VISIBLE_CLASS, shouldShow);
      setHidden(element, !shouldShow);
    });

    setHidden(root.querySelector('.tee-customize-main-form'), !currentStep.formStep);
    setHidden(root.querySelector('.tee-form-header'), true);

    // The original TeeInBlue Add to Cart/quantity area appears only at the end.
    setHidden(root.querySelector('#teeFormActions'), !currentStep.final);

    // Enhance the existing TeeInBlue button without replacing it, so its
    // original click handler and personalization/order payload stay intact.
    const addToCartButton = root.querySelector('#teeAtcButton');
    if (addToCartButton && !addToCartButton.querySelector('.pk-tib-atc-icon')) {
      addToCartButton.insertAdjacentHTML('afterbegin', `
        <span class="pk-tib-atc-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M6.5 8.5h11l-1 11h-9l-1-11Z"></path>
            <path d="M9 9V6.5a3 3 0 0 1 6 0V9"></path>
          </svg>
        </span>
      `);
    }

    const progressPercent = ((currentActiveIndex + 1) / activeSteps.length) * 100;
    wizard.style.setProperty('--pk-step-count', activeSteps.length);
    wizard.querySelector('.pk-tib-wizard__progress-bar').style.width = `${progressPercent}%`;
    wizard.querySelector('.pk-tib-wizard__counter').textContent =
      `Step ${currentActiveIndex + 1} of ${activeSteps.length}`;

    wizard.querySelectorAll('[data-pk-step]').forEach((button) => {
      const buttonStepId = button.dataset.pkStep;
      const definitionIndex = STEP_DEFINITIONS.findIndex(
        (step) => step.id === buttonStepId
      );
      const activeIndex = activeSteps.findIndex((step) => step.id === buttonStepId);
      const isAvailable = activeIndex !== -1;
      const isCurrent = buttonStepId === currentStep.id;
      const isComplete = isAvailable && activeIndex < currentActiveIndex;
      const isUnlocked = isAvailable && definitionIndex <= sharedState.maxUnlockedIndex;

      button.hidden = !isAvailable;
      button.disabled = !isUnlocked;
      button.classList.toggle('is-current', isCurrent);
      button.classList.toggle('is-complete', isComplete);
      button.setAttribute('aria-selected', isCurrent ? 'true' : 'false');
      button.setAttribute('aria-current', isCurrent ? 'step' : 'false');
      if (isAvailable) {
        button.querySelector('.pk-tib-wizard__number').textContent = activeIndex + 1;
      }
    });

    controls.back.hidden = currentActiveIndex === 0;
    controls.next.hidden = currentActiveIndex === activeSteps.length - 1;
    controller.proofMessage.hidden = !currentStep.final;
    controller.guaranteeMessage.hidden = !currentStep.final;

    if (controller.guides) {
      const showPhotoGuide = currentStep.id === 'upload';
      const showSizeGuide =
        currentStep.id === 'size' && !isDigitalFileSelected(root);

      positionGuideControls(controller, currentStep, currentNodes);

      if (controller.guides.photoButton) {
        controller.guides.photoButton.hidden = !showPhotoGuide;
      }
      if (controller.guides.sizeButton) {
        controller.guides.sizeButton.hidden = !showSizeGuide;
      }

      controller.guides.host.hidden = !(showPhotoGuide || showSizeGuide);

      window.clearTimeout(controller.guideTimer);
      window.clearTimeout(controller.guideLateTimer);
      if (showPhotoGuide || showSizeGuide) {
        const reanchorGuide = () => {
          if (
            activeController === controller &&
            sharedState.currentStep === currentStep.id
          ) {
            positionGuideControls(
              controller,
              currentStep,
              getStepNodes(root, currentStep)
            );
          }
        };

        controller.guideTimer = window.setTimeout(reanchorGuide, 220);
        controller.guideLateTimer = window.setTimeout(reanchorGuide, 700);
      }
    }

    if (currentStep.id === 'upload') {
      const petNameField = root.querySelector(
        [
          '.pk-step-pet-name',
          '#tee-field--layer-606222',
          '#tee-field--layer-734186',
          '#tee-field--layer-124087',
          '#tee-field--layer-431318'
        ].join(', ')
      ) || [...root.querySelectorAll('#teeFormInputs > .tee-field')].find(
        (field) => /(?:\bpet\s*name\b|^text$)/i.test(getFieldHeading(field))
      );
      const petNameHeading = petNameField?.querySelector(
        '.tee-field__heading > span:first-child'
      );
      const petNameInput = petNameField?.querySelector('input[type="text"]');

      if (petNameHeading) petNameHeading.textContent = 'Pet Name';
      if (petNameInput) petNameInput.placeholder = 'e.g. Tom, Bruno, etc.';
    }

    clearStepError(controller);
  }

  function goToStep(controller, stepId, shouldScroll = true) {
    const activeSteps = getActiveSteps(controller.root);
    const targetActiveIndex = activeSteps.findIndex((step) => step.id === stepId);
    const targetStaticIndex = STEP_DEFINITIONS.findIndex((step) => step.id === stepId);

    if (targetActiveIndex === -1 || targetStaticIndex > sharedState.maxUnlockedIndex) {
      return;
    }

    sharedState.currentStep = stepId;
    render(controller);
    if (shouldScroll) scrollToWizard(controller);
  }

  function goForward(controller) {
    clearStepError(controller);
    if (!validateCurrentStep(controller)) return;

    const activeSteps = getActiveSteps(controller.root);
    const currentIndex = activeSteps.findIndex(
      (step) => step.id === sharedState.currentStep
    );
    const nextStep = activeSteps[currentIndex + 1];
    if (!nextStep) return;

    const nextStaticIndex = STEP_DEFINITIONS.findIndex(
      (step) => step.id === nextStep.id
    );
    sharedState.maxUnlockedIndex = Math.max(
      sharedState.maxUnlockedIndex,
      nextStaticIndex
    );
    sharedState.currentStep = nextStep.id;
    render(controller);
    scrollToWizard(controller);
  }

  function goBack(controller) {
    clearStepError(controller);

    const activeSteps = getActiveSteps(controller.root);
    const currentIndex = activeSteps.findIndex(
      (step) => step.id === sharedState.currentStep
    );
    const previousStep = activeSteps[currentIndex - 1];
    if (!previousStep) return;

    sharedState.currentStep = previousStep.id;
    render(controller);
    scrollToWizard(controller);
  }

  function buildWizardMarkup() {
    const wizard = document.createElement('section');
    wizard.id = WIZARD_ID;
    wizard.className = 'pk-tib-wizard';
    wizard.setAttribute('aria-label', 'Product personalization steps');

    wizard.innerHTML = `
      <div class="pk-tib-wizard__topline">
        <span class="pk-tib-wizard__hint">Customize your portrait</span>
        <span class="pk-tib-wizard__counter">Step 1 of 5</span>
      </div>
      <div class="pk-tib-wizard__progress" aria-hidden="true">
        <span class="pk-tib-wizard__progress-bar"></span>
      </div>
      <div class="pk-tib-wizard__tabs" role="tablist" aria-label="Personalization progress">
        ${STEP_DEFINITIONS.map((step, index) => `
          <button
            type="button"
            class="pk-tib-wizard__tab${index === 0 ? ' is-current' : ''}"
            data-pk-step="${step.id}"
            role="tab"
            aria-selected="${index === 0 ? 'true' : 'false'}"
            ${index === 0 ? '' : 'disabled'}
          >
            <span class="pk-tib-wizard__number">${index + 1}</span>
            <span>${step.label}</span>
          </button>
        `).join('')}
      </div>
    `;

    return wizard;
  }

  function buildControlsMarkup() {
    const wrapper = document.createElement('div');
    wrapper.className = 'pk-tib-wizard-controls';
    wrapper.innerHTML = `
      <p class="pk-tib-wizard__error" role="alert" hidden></p>
      <p class="pk-tib-final-proof" hidden>
        <span class="pk-tib-final-proof__icon" aria-hidden="true">
          <svg viewBox="0 0 28 28" focusable="false">
            <rect x="5" y="4.5" width="18" height="14" rx="1.5"></rect>
            <circle cx="17.5" cy="9" r="1.5"></circle>
            <path d="m7.5 16 4.5-4 3 2.5 2-2 3.5 3.5"></path>
            <path d="M9 22.5h10M11 18.5l-3 6M17 18.5l3 6"></path>
          </svg>
        </span>
        <span>Order now to see your artwork proof within <strong>12-24 hours</strong></span>
      </p>
      <p class="pk-tib-money-back" hidden>
        <span class="pk-tib-money-back__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path d="M12 3 19 6v5c0 4.6-2.8 8.1-7 10-4.2-1.9-7-5.4-7-10V6l7-3Z"></path>
            <path d="m8.5 12 2.2 2.2 4.8-5"></path>
          </svg>
        </span>
        <span>Love your proof&mdash;or get your money back.</span>
      </p>
      <div class="pk-tib-wizard-controls__buttons">
        <button
          type="button"
          class="pk-tib-button pk-tib-button--back"
          aria-label="Back"
          title="Back"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="m15 18-6-6 6-6"></path>
          </svg>
        </button>
        <button type="button" class="pk-tib-button pk-tib-button--next">
          Next step <span aria-hidden="true">→</span>
        </button>
      </div>
    `;
    return wrapper;
  }

  function attachGuideControls(root, controlsElement) {
    const productInfo = root.closest('[id^="ProductInfo-"]') || document;
    const wrapper = productInfo.querySelector('.guide-links-wrapper');
    if (!wrapper) return null;

    const photoButton = wrapper.querySelector('[data-guide-title="Photo Guide"]');
    const sizeButton = wrapper.querySelector('[data-guide-title="Size Guide"]');
    if (!photoButton && !sizeButton) return null;

    const originalParent = wrapper.parentElement;
    const originalNextSibling = wrapper.nextSibling;
    const host = document.createElement('div');
    host.className = 'pk-tib-step-guides';
    host.hidden = true;

    controlsElement.insertAdjacentElement('beforebegin', host);
    host.append(wrapper);

    const sourceBecameEmpty =
      originalParent && originalParent.children.length === 0;
    if (sourceBecameEmpty) {
      originalParent.classList.add('pk-tib-guide-source-empty');
    }

    return {
      host,
      wrapper,
      photoButton,
      sizeButton,
      originalParent,
      originalNextSibling,
      sourceBecameEmpty
    };
  }

  function positionGuideControls(controller, currentStep, currentNodes) {
    if (!controller.guides) return;

    const { root, guides } = controller;
    const sizeOption = currentStep.id === 'size'
      ? currentNodes.reduce((match, node) => (
          match ||
          (node.matches?.('.tee-option--size:not(.sr-only)') ? node : null) ||
          node.querySelector?.('.tee-option--size:not(.sr-only)')
        ), null) || root.querySelector(
          '.tee-variants .tee-option--size:not(.sr-only)'
        )
      : null;

    if (sizeOption) {
      const sizeChoices = sizeOption.querySelector('.tee-option-inner');
      if (guides.host.parentElement !== sizeOption) {
        sizeOption.insertBefore(guides.host, sizeChoices || null);
      }
    } else {
      const stepContainer = currentStep.formStep
        ? root.querySelector('.tee-customize-main-form')
        : currentNodes[0];

      if (stepContainer && guides.host.nextElementSibling !== stepContainer) {
        stepContainer.insertAdjacentElement('beforebegin', guides.host);
      }
    }

    // TeeInBlue can rebuild the size selector and relocate unknown elements.
    // Re-anchor the original guide block above the active step content.
    if (guides.wrapper.parentElement !== guides.host) {
      guides.host.append(guides.wrapper);
    }
  }

  function createController(root) {
    if (activeController) activeController.destroy();

    // Keep the guard active for any TeeInBlue form replacements, but cancel
    // the fail-safe now that the wizard has successfully initialized.
    document.documentElement.classList.add('pk-tib-wizard-guard');
    window.clearTimeout(window.pkTibWizardGuardTimer);
    delete window.pkTibWizardGuardTimer;

    root.classList.add('pk-tib-wizard-enabled');

    const wizard = buildWizardMarkup();
    const controlsElement = buildControlsMarkup();
    const priceBlock = root.querySelector('.tee-product-price');
    const priceOriginalParent = priceBlock?.parentElement || null;
    const priceOriginalNextSibling = priceBlock?.nextSibling || null;
    const actions = root.querySelector('#teeFormActions');
    const actionsOriginalParent = actions?.parentElement || null;
    const actionsOriginalNextSibling = actions?.nextSibling || null;

    if (priceBlock) {
      priceBlock.insertAdjacentElement('afterend', wizard);
    } else {
      root.prepend(wizard);
    }

    if (actions) {
      actions.insertAdjacentElement('beforebegin', controlsElement);
      controlsElement
        .querySelector('.pk-tib-wizard-controls__buttons')
        .append(actions);
    } else {
      root.append(controlsElement);
    }

    const guides = attachGuideControls(root, controlsElement);

    // Keep TeeInBlue's real price element so variant and option price changes
    // continue updating normally, but present it as the subtotal above the
    // wizard navigation controls.
    if (priceBlock) {
      priceBlock.classList.add('pk-tib-price-bottom');
      controlsElement.insertAdjacentElement('beforebegin', priceBlock);
    }

    const controller = {
      root,
      wizard,
      controlsElement,
      guides,
      guideTimer: null,
      guideLateTimer: null,
      guideObserver: null,
      controls: {
        back: controlsElement.querySelector('.pk-tib-button--back'),
        next: controlsElement.querySelector('.pk-tib-button--next')
      },
      proofMessage: controlsElement.querySelector('.pk-tib-final-proof'),
      guaranteeMessage: controlsElement.querySelector('.pk-tib-money-back'),
      error: controlsElement.querySelector('.pk-tib-wizard__error'),
      refresh: null,
      destroy: null
    };

    const handleTabClick = (event) => {
      const button = event.target.closest('[data-pk-step]');
      if (!button || button.disabled) return;
      goToStep(controller, button.dataset.pkStep);
    };

    const handleOptionChange = (event) => {
      clearStepError(controller);
      if (event.target.closest('.tee-available-products')) {
        window.setTimeout(() => render(controller), 80);
      }
    };

    const handleTextInput = () => clearStepError(controller);

    wizard.addEventListener('click', handleTabClick);
    controller.controls.next.addEventListener('click', () => goForward(controller));
    controller.controls.back.addEventListener('click', () => goBack(controller));
    root.addEventListener('change', handleOptionChange);
    root.addEventListener('input', handleTextInput);

    if (guides) {
      // TeeInBlue rebuilds the radio-size markup during variant and viewport
      // updates. Reattach the guide in the same mutation cycle so it never
      // flashes below the navigation buttons on desktop.
      controller.guideObserver = new MutationObserver(() => {
        const currentStep = getStepDefinition(sharedState.currentStep);
        if (!currentStep || !['size', 'upload'].includes(currentStep.id)) return;

        positionGuideControls(
          controller,
          currentStep,
          getStepNodes(root, currentStep)
        );
      });
      controller.guideObserver.observe(root, {
        childList: true,
        subtree: true
      });
    }

    controller.refresh = () => render(controller);
    controller.destroy = () => {
      root.removeEventListener('change', handleOptionChange);
      root.removeEventListener('input', handleTextInput);
      wizard.removeEventListener('click', handleTabClick);
      window.clearTimeout(controller.guideTimer);
      window.clearTimeout(controller.guideLateTimer);
      controller.guideObserver?.disconnect();

      // Return TeeInBlue's native action block to its original location before
      // removing the wizard controls. This keeps rebuilds and theme-editor
      // refreshes fully reversible.
      if (actions?.isConnected && actionsOriginalParent?.isConnected) {
        const nextSibling =
          actionsOriginalNextSibling?.parentNode === actionsOriginalParent
            ? actionsOriginalNextSibling
            : null;
        actionsOriginalParent.insertBefore(actions, nextSibling);
      }

      if (priceBlock) {
        priceBlock.classList.remove('pk-tib-price-bottom');
        if (priceOriginalParent?.isConnected) {
          const nextSibling =
            priceOriginalNextSibling?.parentNode === priceOriginalParent
              ? priceOriginalNextSibling
              : null;
          priceOriginalParent.insertBefore(priceBlock, nextSibling);
        }
      }

      if (guides) {
        if (guides.originalParent?.isConnected) {
          const nextSibling =
            guides.originalNextSibling?.parentNode === guides.originalParent
              ? guides.originalNextSibling
              : null;
          guides.originalParent.insertBefore(guides.wrapper, nextSibling);
          guides.originalParent.classList.remove('pk-tib-guide-source-empty');
        }
        guides.host.remove();
      }

      wizard.remove();
      controlsElement.remove();
      delete root.dataset.pkCurrentStep;
      root.classList.remove('pk-tib-wizard-enabled');
    };

    activeController = controller;
    render(controller);
  }

  function boot() {
    const root = document.querySelector(ROOT_SELECTOR);
    if (!root) return;

    if (
      activeController &&
      activeController.root === root &&
      root.querySelector(`#${WIZARD_ID}`)
    ) {
      activeController.refresh();
      return;
    }

    createController(root);
  }

  function scheduleBoot() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(boot, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // TeeInBlue is injected asynchronously and may replace option markup after
  // variant changes. Reconnect the wizard whenever that happens.
  const observer = new MutationObserver((mutations) => {
    const affectsTeeInBlue = mutations.some((mutation) => {
      const target = mutation.target.nodeType === 1
        ? mutation.target
        : mutation.target.parentElement;

      if (target?.matches?.(ROOT_SELECTOR) || target?.closest?.(ROOT_SELECTOR)) {
        return true;
      }

      return [...mutation.addedNodes].some((node) => (
        node.nodeType === 1 &&
        (node.matches?.(ROOT_SELECTOR) || node.querySelector?.(ROOT_SELECTOR))
      ));
    });

    if (affectsTeeInBlue) scheduleBoot();
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
